import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'semantic-ui-react';

const helpBotMessages = defineMessages({
  openHelpChat: {
    id: 'helpbot.open',
    defaultMessage: "Obrir xat d'ajuda",
  },
  closeHelpChat: {
    id: 'helpbot.close',
    defaultMessage: "Tancar xat d'ajuda",
  },
  helpTitle: {
    id: 'helpbot.title',
    defaultMessage: 'Ajuda Junts',
  },
  welcomeMessage: {
    id: 'helpbot.welcome',
    defaultMessage: 'Hola! En què et puc ajudar? Escriu el teu dubte aquí.',
  },
  inputPlaceholder: {
    id: 'helpbot.input.placeholder',
    defaultMessage: 'Escriu el teu dubte aquí...',
  },
  inputLabel: {
    id: 'helpbot.input.label',
    defaultMessage: 'Escriu el teu dubte aquí',
  },
  sendButton: {
    id: 'helpbot.send',
    defaultMessage: 'Enviar',
  },
  chatDescription: {
    id: 'helpbot.chat.description',
    defaultMessage:
      "Xat d'ajuda de Junts. Escriu el teu dubte i rebràs informació de contacte.",
  },
  chatConversation: {
    id: 'helpbot.chat.conversation',
    defaultMessage: "Conversa del xat d'ajuda",
  },
  inputForm: {
    id: 'helpbot.input.form',
    defaultMessage: 'Formulari per enviar missatge',
  },
  inputHint: {
    id: 'helpbot.input.hint',
    defaultMessage:
      'Escriu el teu dubte i prem Enter o fes clic a Enviar per rebre ajuda',
  },
  userLabel: {
    id: 'helpbot.user.label',
    defaultMessage: 'Tu',
  },
  botLabel: {
    id: 'helpbot.bot.label',
    defaultMessage: 'Assistent',
  },
});

interface Message {
  id: number;
  text?: string;
  component?: React.ReactNode;
  sender: 'user' | 'bot';
}

const HelpBot: React.FC = () => {
  const intl = useIntl();
  const token = useSelector((state: any) => state.userSession.token);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [showContactMessage, setShowContactMessage] = useState(false);

  // Refs per a la gestió del focus
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Efectes per a la gestió del focus i accessibilitat
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Focus en el camp d'entrada quan s'obre el xat
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    // Scroll automàtic quan hi ha nous missatges
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Gestió de tecles Escape i clic fora del modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseChat();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        chatRef.current &&
        !chatRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        handleCloseChat();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      // Missatge inicial del bot
      setMessages([
        {
          id: 1,
          text: intl.formatMessage(helpBotMessages.welcomeMessage),
          sender: 'bot',
        },
      ]);
    }
  };

  const handleSendMessage = () => {
    if (inputText.trim() === '') return;

    // Afegir missatge de l'usuari
    const userMessage: Message = {
      id: messages.length + 1,
      text: inputText,
      sender: 'user',
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');

    // Mostrar missatge de contacte després d'un petit retard

    setTimeout(() => {
      const botResponse: Message = {
        id: messages.length + 2,
        component: (
          <FormattedMessage
            id="helpbot.contact.autoresponse"
            defaultMessage="Gràcies per la teva consulta! Per rebre ajuda personalitzada, posa't en contacte amb nosaltres a: <e>politicamunicipal@junts.cat</e>"
            values={{
              e: (chunks) => (
                <a
                  target="_blank"
                  href="mailto:politicamunicipal@junts.cat"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        ),
        sender: 'bot',
      };
      setMessages((prev) => [...prev, botResponse]);
      setShowContactMessage(true);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    // Retornar el focus al botó toggle
    setTimeout(() => toggleRef.current?.focus(), 100);
    // Reset del xat després de tancar
    setTimeout(() => {
      setMessages([]);
      setShowContactMessage(false);
    }, 300);
  };

  if (!token) {
    return null;
  }

  console.log('messages', messages);
  return (
    <div className="help-bot">
      {/* Botó flotant */}
      <button
        ref={toggleRef}
        className="help-bot__toggle"
        onClick={handleToggleChat}
        aria-label={
          isOpen
            ? intl.formatMessage(helpBotMessages.closeHelpChat)
            : intl.formatMessage(helpBotMessages.openHelpChat)
        }
        aria-expanded={isOpen}
        aria-controls={isOpen ? 'help-chat-dialog' : undefined}
      >
        <div className="help-bot__icon" aria-hidden="true">
          {isOpen ? '×' : '?'}
        </div>
      </button>

      {/* Finestra del xat */}
      {isOpen && (
        <div
          ref={chatRef}
          id="help-chat-dialog"
          className="help-bot__chat"
          role="dialog"
          aria-labelledby="help-chat-title"
          aria-describedby="help-chat-description"
        >
          <div className="help-bot__header">
            <h4 id="help-chat-title">
              {intl.formatMessage(helpBotMessages.helpTitle)}
            </h4>
            <button
              className="help-bot__close"
              onClick={handleCloseChat}
              aria-label={intl.formatMessage(helpBotMessages.closeHelpChat)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <div
            ref={messagesRef}
            className="help-bot__messages"
            role="log"
            aria-live="polite"
            aria-label={intl.formatMessage(helpBotMessages.chatConversation)}
          >
            <div id="help-chat-description" className="sr-only">
              {intl.formatMessage(helpBotMessages.chatDescription)}
            </div>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`help-bot__message help-bot__message--${message.sender}`}
                role={message.sender === 'bot' ? 'status' : undefined}
                aria-label={`${
                  message.sender === 'user'
                    ? intl.formatMessage(helpBotMessages.userLabel)
                    : intl.formatMessage(helpBotMessages.botLabel)
                }: ${message.text}`}
              >
                <div className="help-bot__message-content">
                  {message.text || message.component}
                </div>
              </div>
            ))}
          </div>

          {!showContactMessage && (
            <div
              className="help-bot__input"
              role="form"
              aria-label={intl.formatMessage(helpBotMessages.inputForm)}
            >
              <label htmlFor="help-chat-input" className="sr-only">
                {intl.formatMessage(helpBotMessages.inputLabel)}
              </label>
              <input
                ref={inputRef}
                id="help-chat-input"
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={intl.formatMessage(
                  helpBotMessages.inputPlaceholder,
                )}
                className="help-bot__input-field"
                aria-describedby="help-chat-input-hint"
                maxLength={500}
              />
              <div id="help-chat-input-hint" className="sr-only">
                {intl.formatMessage(helpBotMessages.inputHint)}
              </div>
              <Button
                primary
                onClick={handleSendMessage}
                disabled={inputText.trim() === ''}
                className="help-bot__send-button"
                aria-label={`${intl.formatMessage(helpBotMessages.sendButton)}${
                  inputText ? ': ' + inputText : ''
                }`}
              >
                {intl.formatMessage(helpBotMessages.sendButton)}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpBot;
